<template>
  <div>
    <div class="p-2">
      <div v-if="!order.id" class="p-3 text-center text-muted">No order selected</div>
      <div v-else>
        <div class="verify-header d-flex justify-content-between align-items-start mb-3">
          <div>
            <div class="small text-muted">Vendor</div>
            <div class="h6 mb-1">{{ order.vendor?.first_name || '' }} {{ order.vendor?.last_name || '' }}</div>
            <div class="small text-muted">{{ order.vendor?.company_name || order.vendor?.external_id || '' }}</div>
            <div class="small mt-1">{{ order.vendor?.phone || '' }}{{ order.vendor?.email ? ' • ' + order.vendor.email : '' }}</div>
            <div class="mt-3 verify-po-status">
              <PoStatus :status="order.status || 'pending'" :showIcon="true" />
            </div>
          </div>
          <div class="text-end">
            <div class="small text-muted">Purchase Order</div>
            <div class="h6 mb-1">{{ order.name || order.external_id }}</div>
            <div class="small">PO#: {{ order.external_id || '-' }}</div>
            <div class="small">Created: {{ order.created_at || '-' }}</div>
            <div class="small">Expected: {{ order.expected_delivery_date || '-' }}</div>
            <div class="h6 mt-2">{{ formattedTotal }}</div>
          </div>
        </div>

        <div class="mb-3">
          <div class="small text-muted">Order Description</div>
          <div class="text-dark">{{ order.description || order.notes || '-' }}</div>
        </div>

        <div class="mb-4">
          <div class="table-responsive">
            <table class="custom-order-table table-sm table-borderless align-middle mb-0">
              <thead>
                <tr>
                  <th class="py-2 ps-3" style="font-size: 0.8rem;">Name</th>
                  <th class="py-2" style="font-size: 0.8rem;">Email</th>
                  <th class="py-2" style="font-size: 0.8rem;">Role</th>
                  <th class="py-2 pe-3" style="font-size: 0.8rem;">Phone</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(contact, idx) in contactList" :key="idx" class="border-bottom border-light">
                  <td class="ps-3 py-2">{{ contact.vendor_contact?.name || contact.name || contact.contact_name || [contact.first_name, contact.last_name].filter(Boolean).join(' ') || 'N/A' }}</td>
                  <td class="py-2"><a :href="`mailto:${contact.vendor_contact?.email || contact.email}`" class="text-decoration-none text-primary">{{ contact.vendor_contact?.email || contact.email || 'N/A' }}</a></td>
                  <td class="py-2"><span class="text-secondary small">{{ contact.vendor_contact?.role || contact.role || contact.position || 'N/A' }}</span></td>
                  <td class="pe-3 py-2"><a :href="`tel:${contact.vendor_contact?.phone || contact.phone}`" class="text-decoration-none small text-dark">{{ contact.vendor_contact?.phone || contact.phone || 'N/A' }}</a></td>
                </tr>
                <tr v-if="!contactList.length">
                  <td colspan="4" class="text-center text-dark opacity-50 py-4 small italic">No contacts available for this PO.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="mb-4">
          <div class="small text-muted">Total Amount</div>
          <div class="h6">{{ formattedTotal }}</div>
        </div>

        <div class="table-responsive mb-3">
          <table class="custom-order-table table-sm table-borderless align-middle mb-0">
            <thead>
              <tr>
                <th style="width: 40px;">#</th>
                <th>Item</th>
                <th class="text-end" style="width: 90px;">Qty</th>
                <th class="text-end" style="width: 90px;">Unit</th>
                <th class="text-end" style="width: 120px;">Unit Price</th>
                <th class="text-end" style="width: 120px;">Total</th>
              </tr>
            </thead>
            <tbody>
              <template v-if="order.items && order.items.length">
                <template v-for="(item, index) in order.items" :key="item.id || index">
                  <tr>
                    <td>{{ index + 1 }}</td>
                    <td>
                      <div class="fw-semibold text-dark">{{ getItemName(item) }}</div>
                      <div class="text-muted small">{{ getItemCode(item) }}</div>
                    </td>
                    <td class="text-end">{{ item.quantity || 0 }}</td>
                    <td class="text-end">{{ item.unit || item.get_material?.unit || 'pcs' }}</td>
                    <td class="text-end">{{ formatCurrency(item.unit_price ?? item.price ?? 0) }}</td>
                    <td class="text-end">{{ formatCurrency(item.total_price ?? item.total ?? (item.quantity || 0) * (item.unit_price ?? 0)) }}</td>
                  </tr>
                  <tr v-if="item.material_type === 'chemical' || item.material_type === 'part'">
                    <td colspan="6" class="ps-4 pb-3">
                      <div class="small text-muted mb-2">Additional item details</div>
                      <div class="d-flex flex-column flex-md-row flex-wrap gap-3">
                        <template v-if="item.material_type === 'chemical'">
                          <div class="text-dark small">
                            <span class="fw-semibold">SDS required:</span> {{ item.sds === 'yes' ? 'Yes' : 'No' }}
                          </div>
                          <div class="text-dark small">
                            <span class="fw-semibold">Approved chemical:</span> {{ item.approved_chemical === 'yes' ? 'Yes' : 'No' }}
                          </div>
                          <div class="text-dark small">
                            <span class="fw-semibold">Food grade:</span> {{ item.food_grade_chemical === 'yes' ? 'Yes' : 'No' }}
                          </div>
                          <div class="text-dark small">
                            <span class="fw-semibold">SDS file:</span>
                            <span v-if="item.sds_attachment">
                              <a :href="resolveFileUrl(item.sds_attachment)" target="_blank" class="text-primary text-decoration-none">View SDS</a>
                            </span>
                            <span v-else class="text-muted">N/A</span>
                          </div>
                        </template>
                        <template v-else>
                          <div class="text-dark small">
                            <span class="fw-semibold">Food contact part:</span> {{ item.food_contact_part === 'yes' ? 'Yes' : 'No' }}
                          </div>
                        </template>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="item.checklists && item.checklists.length">
                    <td colspan="6" class="ps-4 pb-3">
                      <div class="small text-muted mb-2">Equipment Sanitation Checklist</div>
                      <div class="d-flex flex-column gap-2">
                        <div v-for="(check, cIdx) in item.checklists" :key="check.id || cIdx" class="d-flex justify-content-between align-items-center gap-2 small">
                          <div>{{ cIdx + 1 }}. {{ check.label }}</div>
                          <div>
                            <span :class="['badge', check.value === 'yes' ? 'bg-success' : check.value === 'no' ? 'bg-danger' : 'bg-warning text-dark']">
                              {{ check.value === 'na' ? 'N/A' : check.value }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </template>
              <tr v-else>
                <td colspan="6" class="text-center text-muted py-3">No items found</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="d-flex justify-content-end align-items-center py-2 px-1">
          <div class="fw-bold text-dark">
            Grand Total: <span class="text-primary">{{ formattedTotal }}</span>
          </div>
        </div>

        <div v-if="imageList.length || fileList.length" class="mb-3">
          <label class="form-label form-label-thin">Attachments</label>
          <div class="d-flex flex-wrap gap-2">
            <div v-for="image in imageList" :key="image.id" class="attachment-tile text-center" style="width: 100px; cursor: pointer;" @click="openFile(image.file_path)">
              <div class="attachment-preview mb-2 rounded border d-flex align-items-center justify-content-center" style="width: 100%; height: 80px; overflow: hidden;">
                <img :src="resolveFileUrl(image.file_path)" :alt="image.file_name" style="max-width: 100%; max-height: 100%; object-fit: cover;" />
              </div>
              <small class="text-truncate" style="display: block; width: 100%; font-size: 11px;">{{ image.file_name || `Image ${image.id || '1'}` }}</small>
            </div>
            <div v-for="file in fileList" :key="file.id" class="attachment-tile text-center" style="width: 100px; cursor: pointer;" @click="openFile(file.file_path)">
              <div class="attachment-preview mb-2 rounded border d-flex align-items-center justify-content-center" style="width: 100%; height: 80px;">
                <i :class="getFileIcon(file.file_name)" class="fa-2x text-dark opacity-50"></i>
              </div>
              <small class="text-truncate" style="display: block; width: 100%; font-size: 11px;">{{ file.file_name || `File ${file.id || '1'}` }}</small>
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="close">Cancel</button>
          <button type="button" class="btn btn-sm btn-primary" :disabled="submitting" @click="approve">{{ submitting ? 'Approving...' : 'Approve' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'
import { resolveFileUrl as resolveAssetFileUrl } from '@/utils/fileUrl'
import PoStatus from '@/components/PoStatus.vue'

export default {
  name: 'ApproveOrderModal',
  components: { PoStatus },
  data() {
    return {
      order: {},
      submitting: false
    }
  },
  computed: {
    vendorName() {
      const v = this.order.vendor || {}
      return [v.first_name, v.last_name].filter(Boolean).join(' ') || v.external_id || 'N/A'
    },
    contactList() {
      return this.order.purchase_order_vendor_contacts ||  []
    },
    formattedTotal() {
      return this.order.total_price ? ('$' + Number(this.order.total_price).toFixed(2)) : '-'
    },
    receiverTeam() {
      return (this.order.purchase_order_user_roles || []).filter(entry => String(entry.role).toLowerCase() === 'received_by').map(entry => entry.user || { id: entry.user_id, first_name: entry.first_name, last_name: entry.last_name })
    },
    verificationTeam() {
      return (this.order.purchase_order_user_roles || []).filter(entry => String(entry.role).toLowerCase() === 'verification_by').map(entry => entry.user || { id: entry.user_id, first_name: entry.first_name, last_name: entry.last_name })
    },
    imageList() { return this.order.images || [] },
    fileList()  { return this.order.files || [] }
  },
  methods: {
    setOrder(order) {
      const normalized = this.normalizeOrderPayload(order)
      this.order = normalized || {}
    },
    normalizeOrderPayload(payload) {
      if (!payload || typeof payload !== 'object') return null

      if (payload.order && typeof payload.order === 'object') {
        return this.normalizeOrderPayload(payload.order)
      }

      if (payload.purchase_order && typeof payload.purchase_order === 'object') {
        return this.normalizeOrderPayload(payload.purchase_order)
      }

      if (payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)) {
        return this.normalizeOrderPayload(payload.data)
      }

      if (Array.isArray(payload.list) && payload.list.length) {
        return this.normalizeOrderPayload(payload.list[0])
      }

      if (payload.id || payload.external_id || payload.name || payload.vendor || payload.items || payload.purchase_order_user_roles || payload.purchaseOrderVendorContacts) {
        return payload
      }

      return null
    },
    close() {
      this.$emit('close')
    },
    getFullName(user) {
      if (!user) return 'N/A'
      if (typeof user === 'string' || typeof user === 'number') return String(user)
      return [user.first_name, user.last_name].filter(Boolean).join(' ') || user.external_id || 'N/A'
    },
    getItemName(item) {
      const m = item.get_material || item.itemable || item.material || {}
      return m.name || m.code || item.itemable_name || item.name || `Item #${item.itemable_id || item.id}`
    },
    getItemCode(item) {
      const m = item.get_material || item.itemable || item.material || {}
      return m.code || m.external_id || ''
    },
    formatCurrency(amount) {
      if (amount === null || amount === undefined || amount === '') return '$0.00'
      return `$${Number(amount).toFixed(2)}`
    },
    getFileIcon(fileName) {
      if (!fileName) return 'fa fa-file'
      const ext = String(fileName).split('.').pop().toLowerCase()
      const map = {
        pdf: 'fa fa-file-pdf-o',
        doc: 'fa fa-file-word-o',
        docx: 'fa fa-file-word-o',
        xls: 'fa fa-file-excel-o',
        xlsx: 'fa fa-file-excel-o',
        csv: 'fa fa-file-excel-o',
        ppt: 'fa fa-file-powerpoint-o',
        pptx: 'fa fa-file-powerpoint-o',
        jpg: 'fa fa-file-image-o',
        jpeg: 'fa fa-file-image-o',
        png: 'fa fa-file-image-o',
        gif: 'fa fa-file-image-o',
        webp: 'fa fa-file-image-o',
        txt: 'fa fa-file-text-o',
        md: 'fa fa-file-text-o',
        zip: 'fa fa-file-archive-o',
        rar: 'fa fa-file-archive-o'
      }
      return map[ext] || 'fa fa-file-o'
    },
    resolveFileUrl(path) {
      return resolveAssetFileUrl(path)
    },
    openFile(filePath) {
      const url = this.resolveFileUrl(filePath)
      if (!url) return
      const ext = (filePath || '').split('.').pop().toLowerCase()
      const officeFormats = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx']
      if (officeFormats.includes(ext)) {
        window.open(`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`, '_blank')
      } else {
        window.open(url, '_blank')
      }
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
    },
    async approve() {
      if (!this.order || !this.order.id) return
      this.submitting = true
      try {
        const payload = { purchase_order_id: this.order.id }
        const result = await form_api_submit({ url: 'purchase-order/approve', method: 'POST', data: payload, show_success_toast: true, show_error_toast: true })
        if (result && result.success) {
          this.$emit('approved', result.data)
          this.close()
        }
      } catch (e) {
        console.error('Approve error', e)
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.custom-order-table {
  width: 100% !important;
  background: transparent !important;
  border-collapse: separate !important;
  border-spacing: 0 !important;
}

.custom-order-table thead th {
  background: #f8fafc !important;
  color: #475569 !important;
  border-bottom: 1px solid #e2e8f0 !important;
  font-weight: 600 !important;
}

.custom-order-table tbody tr td {
  background: transparent !important;
  color: inherit !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.custom-order-table tbody tr:nth-child(even) td,
.custom-order-table tbody tr:nth-child(odd) td {
  background: transparent !important;
}
</style>
